import React from 'react';
import { cn } from '@hero/web-lib';
import { InputLabel } from '../../../ui/Input';
import { FinancingFieldBadge } from '../../FinancingFieldBadge';
import type { FinancingFieldBadgeVariant } from '../../FinancingFieldBadge';
import { LogistiFieldBadge } from '../../LogistiFieldBadge';
import type { LogistiFieldBadgeVariant } from '../../LogistiFieldBadge';

export interface FieldShellProps {
  /** Resolved label shown above the control. */
  label: string;
  /**
   * Optional helper / description sub-text rendered under the
   * input. Keep it short — one line of copy that explains *why*
   * the field matters (e.g. "Used by lenders to verify the
   * carrier's commercial registration").
   */
  helper?: string;
  /**
   * If set, renders the financing chip next to the label.
   * `'required'` (red) for hard-required fields, `'recommended'`
   * (green) for soft-required.
   */
  financing?: FinancingFieldBadgeVariant;
  /**
   * If set, renders the Logisti chip next to the label. Both chips can be
   * present at once — a field can matter to financing and to Bayan.
   */
  logisti?: LogistiFieldBadgeVariant;
  /**
   * Whether the underlying field currently carries a value. When
   * `true` the financing chip flips to its green "Provided"
   * state — see {@link FinancingFieldBadge} for the tri-state
   * colour table.
   *
   * The RHF field wrappers (`RhfTextField`, `RhfImageField`)
   * compute this from `field.value` automatically; bare consumers
   * can pass an inline boolean.
   */
  financingSatisfied?: boolean;
  /** Mirrors the InputLabel `required` flag — renders the `*`. */
  required?: boolean;
  /**
   * Validation error string rendered below the helper. Sourced
   * from RHF `fieldState.error?.message`.
   */
  error?: string;
  /**
   * Optional `id` to wire the label's `htmlFor` to. When omitted
   * we don't render an `htmlFor` (still accessible because the
   * `<input>` itself owns its own label-by-proximity).
   */
  htmlFor?: string;
  /** Pass-through className for layout (column span, width…). */
  className?: string;
  children: React.ReactNode;
}

/**
 * `FieldShell` — internal layout primitive shared by every
 * Org-profile field component.
 *
 * Responsibilities:
 *  - Renders the label with an optional `required` asterisk via
 *    `InputLabel`.
 *  - Pins the financing chip to the end of the label row when
 *    `financing` is set, so all org-profile fields render their
 *    "Required/Recommended for financing" hint in the same
 *    consistent spot.
 *  - Shows the validation error below the control, in the same
 *    style as `FormMessage` in the `ui/Form` primitive.
 *  - Shows an optional `helper` description above the error.
 *
 * Kept colocated under `OrgProfileFields/shared/` because no other
 * surface needs this exact layout — keeping it private avoids
 * polluting the `app/` export surface with a half-baked field shell.
 */
export function FieldShell({
  label,
  helper,
  financing,
  logisti,
  financingSatisfied,
  required,
  error,
  htmlFor,
  className,
  children,
}: FieldShellProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <InputLabel label={label} required={required} htmlFor={htmlFor} className="mb-0" />
        <div className="flex items-center gap-1.5">
          {financing ? (
            <FinancingFieldBadge variant={financing} satisfied={financingSatisfied} />
          ) : null}
          {logisti ? <LogistiFieldBadge variant={logisti} /> : null}
        </div>
      </div>
      {children}
      {helper && !error ? (
        <p className="text-xs text-muted-foreground">{helper}</p>
      ) : null}
      {error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  );
}
